import React from 'react';
import {
  createGlobalStyle,
  ThemeProvider as StyledThemeProvider,
} from 'styled-components';

// Extend the default theme interface
declare module 'styled-components' {
  export interface DefaultTheme {
    colors: {
      primary: string;
      secondary: string;
      info: string;
      background: string;
      gradient: {
        start: string;
        end: string;
      };
      text: {
        primary: string;
        secondary: string;
        muted: string;
      };
      white: string;
      black: string;
      black2: string;
      input: {
        border: string;
        background: string;
      };
      checkbox: {
        checked: string;
      };
      reminderOrange: string;
      actionBlue: string;
      eventPurple: string;
      error: string;
    };
    gradients: {
      background: string;
      primary: string;
      subtle: string;
    };
    typography: {
      h1: {
        size: string;
        weight: number;
        lineHeight: number;
      };
      h2: {
        size: string;
        weight: number;
        lineHeight: number;
      };
      body: {
        large: {
          size: string;
          weight: number;
          lineHeight: number;
        };
        default: {
          size: string;
          weight: number;
          lineHeight: number;
        };
        small: {
          size: string;
          weight: number;
          lineHeight: number;
        };
      };
    };
    spacing: {
      xs: string;
      sm: string;
      md: string;
      lg: string;
      xl: string;
    };
    fonts: {
      primary: string;
    };
    sizes: {
      maxWidth: string;
      inputHeight: string;
    };
    breakpoints: {
      xs: string;
      sm: string;
      md: string;
      lg: string;
      xl: string;
    };
  }
}

const GlobalStyle = createGlobalStyle`
  body {
    color: ${(props) => props.theme.colors.text.primary};
    background: ${(props) => props.theme.gradients.background};
    background-color: ${(props) => props.theme.gradients.background};
    font-family: ${(props) => props.theme.fonts.primary};
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    line-height: 1.5;
    margin: 0;
    padding: 0;
    min-height: 100vh;
  }

  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  h1 {
    font-size: ${(props) => props.theme.typography.h1.size};
    font-weight: ${(props) => props.theme.typography.h1.weight};
    line-height: ${(props) => props.theme.typography.h1.lineHeight};
  }

  h2 {
    font-size: ${(props) => props.theme.typography.h2.size};
    font-weight: ${(props) => props.theme.typography.h2.weight};
    line-height: ${(props) => props.theme.typography.h2.lineHeight};
  }

  p {
    font-size: ${(props) => props.theme.typography.body.default.size};
    font-weight: ${(props) => props.theme.typography.body.default.weight};
    line-height: ${(props) => props.theme.typography.body.default.lineHeight};
  }
`;

const theme = {
  colors: {
    primary: '#6D539F', // Deep Purple
    secondary: '#3B82F6', // Bright Blue
    info: '#E4CB76', // Royal Gold
    background: '#0A0A0F', // Off Black
    gradient: {
      start: '#6D539F', // Deep Purple
      end: '#3B82F6', // Bright Blue
    },
    text: {
      primary: '#F0F3FF', // White
      secondary: '#9C9C9D', // Light Gray
      muted: '#9CA3AF', // Muted Gray
    },
    white: '#F0F3FF', // White
    black: '#0C0D0D', // Off Black 2
    black2: '#1A1A2D', // Purpleish Off Black
    input: {
      border: '#2D3748', // Medium Gray
      background: '#1A1A24', // Dark Blue-Gray
    },
    checkbox: {
      checked: '#6D539F', // Deep Purple
    },
    reminderOrange: '#E49976', // Orange
    actionBlue: '#6E9ADD', // Blue
    eventPurple: '#D581CF', // Purple
    error: '#C75B7A', // Reddish-Purple
  },
  gradients: {
    background: `linear-gradient(180deg, #0A0A0F 0%, #1a1a2e 100%)`,
    primary: `linear-gradient(135deg, #6D539F 0%, #3B82F6 100%)`,
    subtle: `linear-gradient(135deg, #0A0A0F 0%, #6D539F10 50%, #0A0A0F 100%)`,
  },
  typography: {
    h1: {
      size: '3.5rem',
      weight: 700,
      lineHeight: 1.2,
    },
    h2: {
      size: '2.5rem',
      weight: 700,
      lineHeight: 1.2,
    },
    body: {
      large: {
        size: '1.125rem',
        weight: 400,
        lineHeight: 1.5,
      },
      default: {
        size: '1rem',
        weight: 400,
        lineHeight: 1.5,
      },
      small: {
        size: '0.875rem',
        weight: 400,
        lineHeight: 1.5,
      },
    },
  },
  spacing: {
    xs: '0.5rem',
    sm: '1rem',
    md: '1.5rem',
    lg: '2rem',
    xl: '3rem',
  },
  fonts: {
    primary:
      '-apple-system, BlinkMacSystemFont, "SF Pro Text", "SF Pro Display", system-ui, Helvetica, Arial, sans-serif',
  },
  sizes: {
    maxWidth: '500px',
    inputHeight: '60px',
  },
  breakpoints: {
    xs: '0px',
    sm: '600px',
    md: '900px',
    lg: '1200px',
    xl: '1536px',
  },
} as const;

type ThemeProviderProps = {
  children: React.ReactNode;
};

export const ThemeProvider = ({ children }: ThemeProviderProps) => (
  <StyledThemeProvider theme={theme}>
    <GlobalStyle />
    {children}
  </StyledThemeProvider>
);
export { theme };
